// Global caches to ensure instantaneous render without network delays
window.__cartRecsCache = window.__cartRecsCache || {};
window.__cartRecsInFlight = window.__cartRecsInFlight || {};
window.__cartRecsLastHtml = window.__cartRecsLastHtml || null;

class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.sectionId = 'cart-drawer-recommendations';
    this.limit = parseInt(this.dataset.limit) || 4;
    this.isFetching = false;
  }

  connectedCallback() {
    this.init();
    this.observeCartDrawer();
  }

  observeCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || this._hasDrawerObserver) return;
    this._hasDrawerObserver = true;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (cartDrawer.classList.contains('active')) {
            // When opened, initialize immediately if empty or not rendered
            if (!this.querySelector('[data-cart-recommendations-rendered]')) {
              this.init();
            }
          }
        }
      }
    });

    observer.observe(cartDrawer, { attributes: true });
  }

  init() {
    // If empty-cart recommendations block, only attach listeners
    if (this.dataset.isEmpty === 'true') {
      this.attachEventListeners();
      return;
    }

    const productId = this.getProductId();
    if (!productId) {
      this.handleEmptyOrError();
      return;
    }

    // 1. Instant cache hit: render immediately with ZERO network latency
    if (window.__cartRecsCache[productId]) {
      this.renderContent(window.__cartRecsCache[productId]);
      return;
    }

    // 2. If we have previous recommendations from earlier in the session,
    // display them immediately so the drawer NEVER hangs on an empty spinner!
    if (window.__cartRecsLastHtml && !this.querySelector('[data-cart-recommendations-rendered]')) {
      this.renderContent(window.__cartRecsLastHtml);
    }

    // 3. Fetch fresh recommendations in background
    this.fetchRecommendations(productId);
  }

  getProductId() {
    // Priority 1: Direct data attribute on this element (from Liquid server-side)
    if (this.dataset.productId && this.dataset.productId !== 'null' && this.dataset.productId !== '') {
      return this.dataset.productId;
    }

    // Priority 2: From first cart item row in drawer
    const firstItem = document.querySelector('.cart-item[data-product-id]');
    if (firstItem && firstItem.dataset.productId) {
      return firstItem.dataset.productId;
    }

    // Priority 3: Any cart item element with data-product-id
    const cartItem = document.querySelector('[data-cart-item][data-product-id]');
    if (cartItem && cartItem.dataset.productId) {
      return cartItem.dataset.productId;
    }

    return null;
  }

  async fetchRecommendations(productId) {
    if (!productId || this.isFetching) return;

    // Check cache again in case another instance just filled it
    if (window.__cartRecsCache[productId]) {
      this.renderContent(window.__cartRecsCache[productId]);
      return;
    }

    // Reuse in-flight request if one is already running for this product
    if (window.__cartRecsInFlight[productId]) {
      try {
        const html = await window.__cartRecsInFlight[productId];
        if (html) {
          this.renderContent(html);
        }
      } catch (e) {
        this.handleEmptyOrError();
      }
      return;
    }

    this.isFetching = true;

    // 6 second timeout to guarantee it NEVER hangs indefinitely
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    window.__cartRecsInFlight[productId] = (async () => {
      try {
        const url = `/recommendations/products?section_id=${this.sectionId}&product_id=${productId}&limit=${this.limit}`;
        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('HTTP ' + response.status);
        }

        const text = await response.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const rendered = doc.querySelector('[data-cart-recommendations-rendered]');

        if (rendered && rendered.querySelectorAll('.cart-drawer__recommendations-item').length > 0) {
          const html = rendered.outerHTML;
          window.__cartRecsCache[productId] = html;
          window.__cartRecsLastHtml = html;
          return html;
        }
        return null;
      } catch (err) {
        clearTimeout(timeoutId);
        console.warn('CartDrawerRecommendations:', err);
        return null;
      } finally {
        delete window.__cartRecsInFlight[productId];
      }
    })();

    try {
      const html = await window.__cartRecsInFlight[productId];
      this.isFetching = false;
      if (html) {
        this.renderContent(html);
      } else {
        this.handleEmptyOrError();
      }
    } catch (e) {
      this.isFetching = false;
      this.handleEmptyOrError();
    }
  }

  renderContent(html) {
    if (!html) return;
    this.innerHTML = html;
    this.attachEventListeners();
  }

  handleEmptyOrError() {
    // If we have any last recommendations from this session, keep them visible!
    if (window.__cartRecsLastHtml) {
      this.renderContent(window.__cartRecsLastHtml);
      return;
    }

    // Otherwise, gracefully hide the spinner placeholder so it NEVER stays stuck!
    const placeholder = this.querySelector('.cart-drawer__recommendations-placeholder');
    if (placeholder) {
      placeholder.style.display = 'none';
    }
  }

  attachEventListeners() {
    this.attachArrowListeners();

    // Quick add to cart buttons
    const addToCartLinks = this.querySelectorAll('.cart-rec-link--add-to-cart');
    addToCartLinks.forEach(link => {
      if (link.dataset.bound === 'true') return;
      link.dataset.bound = 'true';
      link.addEventListener('click', (e) => this.handleAddToCart(e, link));
    });
  }

  attachArrowListeners() {
    const slider = this.querySelector('.cart-drawer__recommendations-slider');
    const prevButton = this.querySelector('.cart-drawer__recommendations-arrow--prev');
    const nextButton = this.querySelector('.cart-drawer__recommendations-arrow--next');

    if (!slider || !prevButton || !nextButton) return;
    if (prevButton.dataset.bound === 'true') return;
    prevButton.dataset.bound = 'true';
    nextButton.dataset.bound = 'true';

    prevButton.addEventListener('click', (e) => {
      e.preventDefault();
      const scrollAmount = slider.offsetWidth * 0.8;
      slider.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextButton.addEventListener('click', (e) => {
      e.preventDefault();
      const scrollAmount = slider.offsetWidth * 0.8;
      slider.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });
  }

  async handleAddToCart(e, link) {
    e.preventDefault();

    const variantId = link.dataset.variantId;
    if (!variantId) return;

    const originalText = link.textContent;
    link.textContent = 'Adding...';
    link.style.pointerEvents = 'none';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      // Request sections directly with cart/add so Shopify updates the cart in a single request!
      const cartDrawer = document.querySelector('cart-drawer');
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });

      const data = await response.json();

      if (response.ok && !data.status) {
        link.textContent = 'Added!';

        if (cartDrawer && data.sections) {
          cartDrawer.renderContents(data);
        } else if (cartDrawer) {
          const secRes = await fetch('/?sections=cart-drawer,cart-icon-bubble');
          const secData = await secRes.json();
          cartDrawer.renderContents(secData);
        }

        if (typeof window.refreshCartIconBubble === 'function') {
          window.refreshCartIconBubble();
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        setTimeout(() => {
          link.textContent = originalText;
          link.style.pointerEvents = '';
        }, 1500);
      } else {
        throw new Error(data.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      link.textContent = originalText;
      link.style.pointerEvents = '';
    }
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}